import { ComponentType, createElement } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { ROUTES } from "./routes";

// Component imports (lazy loading for better performance)

// ===== PUBLIC ROUTES =====
import HomePage from "../views/homepage/Homepage";
import Login from "../views/Login/Login";
import TestPage from "../views/testview/testPage";
import NotFound from "../views/NotFound/NotFound";

// ===== MUSIC CONSOLE ROUTES =====
// Music Control Suite

// Music Asset Audits
import MusicAssetAudits from "../views/MusicAssetAudits/MusicAssetAudits";
import MusicVideosRoute from "../views/MusicAssetAudits/subRoutes/MusicVideosRoute";
import StubAssetsRoute from "../views/MusicAssetAudits/subRoutes/StubAssetsRoute";
import IncorrectAssetTypes from "../views/MusicAssetAudits/subRoutes/IncorrectAssetTypes";
import MismatchedSoundRecordingsRoute from "../views/MusicAssetAudits/subRoutes/MismatchedSoundRecordings";
import MusicVideoMetadataRoute from "../views/MusicAssetAudits/subRoutes/MusicVideoMetadataRoute";
import MusicAssetAuditsDashboard from "../views/MusicAssetAudits/subRoutes/MusicAssetAuditsDashboard";
import MusicLabelDashboard from "../views/MusicAssetAudits/subRoutes/MusicLabelDashboard";
import BrandArtistLookupRoute from "../views/MusicAssetAudits/subRoutes/BrandArtistLookupRoute";
import MvMetadataGapsRoute from "../views/MusicAssetAudits/subRoutes/MvMetadataGapsRoute";
import ClaimPolicyPatchDashboard from "../views/ClaimPolicyPatchDashboard/ClaimPolicyPatchDashboard";
import ClaimPolicyPatchOverview from "../views/ClaimPolicyPatchDashboard/subRoutes/ClaimPolicyPatchOverview";
import ClaimPolicyPatchUpdatedClaims from "../views/ClaimPolicyPatchDashboard/subRoutes/ClaimPolicyPatchUpdatedClaims";

// SoundRecording Linking
import SoundRecordings from "src/views/SoundRecordings/SoundRecordings";
import SoundRecordingTableView from "src/views/SoundRecordings/subRoutes/SoundRecordingTableView";
import SoundRecordingLinkingTool from "src/views/SoundRecordings/subRoutes/SoundRecordingLinkingTool";

// Scrapers
import Scrapers from "../views/Scrapers/Scrapers";
import MusicAssetScraper from "../views/Scrapers/subRoutes/MusicAssetScraper";
import MusicAssetScraperExplorer from "../views/Scrapers/subRoutes/MusicAssetScraperExplorer";

// DSR Audits
import DSRAudits from "../views/DSRAudits/DSRAudits";
import DSRAuditsDashboard from "../views/DSRAudits/DSRAuditsDashboard";
import GenEntReportsRoute from "../views/DSRAudits/subRoutes/GenEntReportsRoute";
import MusicVideosReportsRoute from "../views/DSRAudits/subRoutes/MusicVideosReportsRoute";
import SoundRecordingsReportsRoute from "../views/DSRAudits/subRoutes/SoundRecordingsReportsRoute";
import MasterArtistsRoute from "../views/DSRAudits/subRoutes/MasterArtistsRoute";
import SftpBrowserRoute from "../views/DSRAudits/subRoutes/SftpBrowserRoute";

// Composition Audits
import CompositionAudits from "../views/CompositionAudits/CompositionAudits";
import CompositionAuditsDashboard from "../views/CompositionAudits/CompositionAuditsDashboard";
import SyncOwnershipRoute from "../views/MusicAssetAudits/subRoutes/SyncOwnershipRoute";

// ===== YOUTUBE TOOLS ROUTES =====
// YouTube Scorecard
import YouTubeScorecard from "../views/YouTubeScorecard/YouTubeScorecard";
import MetadataTab from "../views/YouTubeScorecard/Tabs/metadataTab";
import ClaimsAdsTab from "../views/YouTubeScorecard/Tabs/claimsAdsTab";
import WhitelistTab from "../views/YouTubeScorecard/Tabs/whitelistTab";
import CustomIdLookupTab from "../views/YouTubeScorecard/Tabs/customIdLookupTab";
import ReferenceStatusTab from "../views/YouTubeScorecard/Tabs/referenceStatusTab";
import MoonbugMusicLabelScorecard from "../views/YouTubeScorecard/MoonbugMusicLabelScorecard";

// Banner Swap (legacy v1) + Banner Dashboard (v2)
import BannerDashboard from "../views/BannerDashboard/BannerDashboard";
import DefaultBannersTab from "../views/BannerDashboard/tabs/DefaultBannersTab";
import SwapQueueTab from "../views/BannerDashboard/tabs/SwapQueueTab";
import CalendarTab from "../views/BannerDashboard/tabs/CalendarTab";

// ===== OTHER TOOLS ROUTES =====
import LabelsDashboard from "../views/LabelDashboard/LabelDashboard";
import SpotifyPodcastUploader from "../views/SpotifyPodcastUploader/SpotifyPodcastUploader";

// Classifiers
import Classifiers from "../views/Classifiers/Classifiers";
import MusicClassifierTab from "../views/OACAudit/MusicClassifierTab";
import MusicClassifierHistoryTab from "../views/OACAudit/MusicClassifierHistoryTab";
import VideoAuditorTab from "../views/OACAudit/VideoAuditorTab";
import BulkAnalysisTab from "../views/OACAudit/BulkAnalysisTab";
import MetadataAudit from "../views/MetadataAudit/MetadataAudit";
import TitleSpellingTab from "../views/MetadataAudit/Tabs/TitleSpellingTab";
import DictionarySettingsTab from "../views/MetadataAudit/Tabs/DictionarySettingsTab";
import SpellcheckAdminTab from "../views/MetadataAudit/Tabs/SpellcheckAdminTab";
import MetadataAuditComparer from "../views/CompositionMetadataAudit/MetadataAuditComparer";

// OAC Audit
import OACAudit from "../views/OACAudit/OACAudit";
import OACAuditTab from "../views/OACAudit/OACAuditTab";
import ChannelSettingsTab from "../views/OACAudit/ChannelSettingsTab";
import PlaylistCreationTab from "../views/OACAudit/PlaylistCreationTab";

// Channel Checkup
import ChannelCheckup from "../views/ChannelCheckup/ChannelCheckup";

// ===== ADMIN ROUTES =====
import AppManagment from "../views/AppManagment/AppManagment";
import BannerSwapAdminOps from "../views/BannerSwapAdminOps/BannerSwapAdminOps";

/** Redirect `/metadata_audit` → default tab */
const MetadataAuditIndexRedirect = () =>
  createElement(Navigate, { to: ROUTES.METADATA_AUDIT_TITLE_QC, replace: true });

/** Legacy path `/metadata_audit/title_spelling` → Title QC tab */
const MetadataAuditTitleSpellingRedirect = () =>
  createElement(Navigate, { to: ROUTES.METADATA_AUDIT_TITLE_QC, replace: true });

/** Keep bookmarks on `/banner_swap/*` working after the nav moved to `/banner_dashboard`. */
function LegacyBannerSwapRedirect({ to }: { to: string }) {
  const location = useLocation();
  return createElement(Navigate, {
    to: { pathname: to, search: location.search, hash: location.hash },
    replace: true,
  });
}

const LegacyBannerSwapLayout = () => createElement(Outlet);

const RedirectLegacyBannerToDefaultBanners = () =>
  createElement(LegacyBannerSwapRedirect, {
    to: ROUTES.BANNER_DASHBOARD_DEFAULT_BANNERS,
  });

const RedirectLegacyBannerToSwapQueue = () =>
  createElement(LegacyBannerSwapRedirect, {
    to: ROUTES.BANNER_DASHBOARD_SWAP_QUEUE,
  });

const RedirectLegacyBannerToDashboard = () =>
  createElement(LegacyBannerSwapRedirect, {
    to: ROUTES.BANNER_DASHBOARD,
  });

const BannerDashboardIndexRedirect = () =>
  createElement(Navigate, {
    to: ROUTES.BANNER_DASHBOARD_DEFAULT_BANNERS,
    replace: true,
  });

// Local RouteConfig interface for routeGenerator.ts
export interface RouteConfigWithComponents {
  path?: string;
  element: ComponentType<any>;
  children?: RouteConfigWithComponents[];
  protected?: boolean;
  adminOnly?: boolean;
  platformsOnly?: boolean;
  index?: boolean;
}

// Route configuration with actual components - SINGLE SOURCE OF TRUTH
export const ROUTE_CONFIG_WITH_COMPONENTS: RouteConfigWithComponents[] = [
  {
    path: ROUTES.LOGIN,
    element: Login,
    protected: false,
  },
  {
    path: ROUTES.ROOT,
    element: Login,
    protected: false,
  },
  {
    path: ROUTES.TEST,
    element: TestPage,
    protected: false,
  },
  {
    path: ROUTES.HOMEPAGE,
    element: HomePage,
    protected: true,
  },
  {
    path: "composition_audits",
    element: CompositionAudits,
    protected: true,
    platformsOnly: true,
    children: [
      {
        index: true,
        element: CompositionAuditsDashboard,
        protected: true,
        platformsOnly: true,
      },
      {
        path: "umpg_sync",
        element: SyncOwnershipRoute,
        protected: true,
        platformsOnly: true,
      },
    ],
  },
  {
    path: ROUTES.YOUTUBE_MUSIC_ASSET_AUDITS,
    element: MusicAssetAudits,
    protected: true,
    platformsOnly: true,
    children: [
      // TODO: Change to correct component once created MusicVideoAuditsDashboard
      // {
      //   index: true,
      //   element: MusicVideoAuditsDashboard,
      //   protected: true,
      //   platformsOnly: true,
      // },
      {
        path: ROUTES.YOUTUBE_MUSIC_ASSET_AUDITS_DASHBOARD,
        element: MusicAssetAuditsDashboard,
        protected: true,
        platformsOnly: true,
      },
      {
        path: ROUTES.MUSIC_LABEL_DASHBOARD,
        element: MusicLabelDashboard,
        protected: true,
        platformsOnly: true,
      },
      {
        path: ROUTES.MUSIC_VIDEO_EMBEDDING,
        element: MusicVideosRoute,
        protected: true,
        platformsOnly: true,
      },

      {
        path: ROUTES.MUSIC_VIDEO_METADATA,
        element: MusicVideoMetadataRoute,
        protected: true,
        platformsOnly: true,
      },
      {
        path: ROUTES.STUB_ASSET_AUDIT,
        element: StubAssetsRoute,
        protected: true,
        platformsOnly: true,
      },
      {
        path: ROUTES.MISMATCHED_SOUND_RECORDINGS,
        element: MismatchedSoundRecordingsRoute,
        protected: true,
        platformsOnly: true,
      },
      {
        path: ROUTES.ASSET_TYPE_AUDIT,
        element: IncorrectAssetTypes,
        protected: true,
        platformsOnly: true,
      },
      {
        path: ROUTES.MV_METADATA_GAPS,
        element: MvMetadataGapsRoute,
        protected: true,
        platformsOnly: true,
      },
      {
        path: ROUTES.BRAND_ARTIST_LOOKUP,
        element: BrandArtistLookupRoute,
        protected: true,
        platformsOnly: true,
      },
    ],
  },
  {
    path: ROUTES.CLAIM_POLICY_PATCH_DASHBOARD,
    element: ClaimPolicyPatchDashboard,
    protected: true,
    platformsOnly: true,
    children: [
      {
        path: ROUTES.CLAIM_POLICY_PATCH_DASHBOARD_OVERVIEW,
        element: ClaimPolicyPatchOverview,
        protected: true,
        platformsOnly: true,
      },
      {
        path: ROUTES.CLAIM_POLICY_PATCH_DASHBOARD_UPDATED_CLAIMS,
        element: ClaimPolicyPatchUpdatedClaims,
        protected: true,
        platformsOnly: true,
      },
    ],
  },
  {
    path: ROUTES.DSR_AUDITS,
    element: DSRAudits,
    protected: true,
    platformsOnly: true,
    children: [
      {
        index: true,
        element: DSRAuditsDashboard,
        protected: true,
        platformsOnly: true,
      },
      {
        path: ROUTES.DSR_GENENT,
        element: GenEntReportsRoute,
        protected: true,
        platformsOnly: true,
      },
      {
        path: ROUTES.DSR_MUSIC_VIDEOS,
        element: MusicVideosReportsRoute,
        protected: true,
        platformsOnly: true,
      },
      {
        path: ROUTES.DSR_SOUND_RECORDINGS,
        element: SoundRecordingsReportsRoute,
        protected: true,
        platformsOnly: true,
      },
      {
        path: ROUTES.DSR_MASTER_ARTISTS,
        element: MasterArtistsRoute,
        protected: true,
        platformsOnly: true,
      },
      {
        path: ROUTES.DSR_SFTP,
        element: SftpBrowserRoute,
        protected: true,
        platformsOnly: true,
      },
    ],
  },

  // {
  //   path: "data_health_tool",
  //   element: MusicAssetAudits,
  //   protected: true,
  //   platformsOnly: true,
  //   children: [
  //     {
  //       index: true,
  //       element: DashboardRoute,
  //       protected: true,
  //       platformsOnly: true,
  //     },
  //     {
  //       path: "sync_ownership",
  //       element: SyncOwnershipRoute,
  //       protected: true,
  //       platformsOnly: true,
  //     },
  //     {
  //       path: "music_videos",
  //       element: MusicVideosRoute,
  //       protected: true,
  //       platformsOnly: true,
  //     },
  //     {
  //       path: "stub_assets",
  //       element: StubAssetsRoute,
  //       protected: true,
  //       platformsOnly: true,
  //     },
  //     {
  //       path: "mismatched_sound_recordings",
  //       element: MismatchedSoundRecordingsRoute,
  //       protected: true,
  //       platformsOnly: true,
  //     },
  //     {
  //       path: "incorrect_asset_types",
  //       element: IncorrectAssetTypes,
  //       protected: true,
  //       platformsOnly: true,
  //     },
  //     {
  //       path: "example_realtime",
  //       element: ExampleRealtimeRoute,
  //       protected: true,
  //       platformsOnly: true,
  //     },
  //   ],
  // },
  // {
  //   path: "dsr_audits",
  //   element: DSRAuditsLegacy,
  //   protected: true,
  //   platformsOnly: true,
  //   children: [
  //     {
  //       index: true,
  //       element: DSRDashboardRoute,
  //       protected: true,
  //       platformsOnly: true,
  //     },
  //     {
  //       path: "genent",
  //       element: GenEntReportsRoute,
  //       protected: true,
  //       platformsOnly: true,
  //     },
  //     {
  //       path: "music_videos",
  //       element: MusicVideosReportsRoute,
  //       protected: true,
  //       platformsOnly: true,
  //     },
  //     {
  //       path: "sound_recordings",
  //       element: SoundRecordingsReportsRoute,
  //       protected: true,
  //       platformsOnly: true,
  //     },
  //   ],
  // },
  {
    path: ROUTES.COMPOSITION_ASSETS,
    element: CompositionAudits,
    protected: true,
    platformsOnly: true,
    children: [
      {
        path: ROUTES.UMPG_SYNC_AUDIT,
        element: SyncOwnershipRoute,
        protected: true,
        platformsOnly: true,
      },
      {
        path: ROUTES.COMPOSITION_GENERAL_INSPECTION,
        element: SyncOwnershipRoute,
        protected: true,
        platformsOnly: true,
      },
      {
        path: ROUTES.COMPOSITION_METADATA_AUDIT_COMPARER,
        element: MetadataAuditComparer,
        protected: true,
        platformsOnly: true,
      },
    ],
  },
  {
    path: ROUTES.SOUND_RECORDINGS,
    element: SoundRecordings,
    protected: true,
    platformsOnly: true,
    children: [
      {
        path: ROUTES.SOUND_RECORDINGS_LINKING_TABLE_VIEW,
        element: SoundRecordingTableView,
        protected: true,
        platformsOnly: true,
      },

      {
        path: ROUTES.SOUND_RECORDINGS_LINKING_TOOL,
        element: SoundRecordingLinkingTool,
        protected: true,
        platformsOnly: true,
      },
    ],
  },
  {
    path: "sound_recording_linking_tool",
    element: SoundRecordingLinkingTool,
    protected: true,
    platformsOnly: true,
  },
  {
    path: ROUTES.SCRAPERS,
    element: Scrapers,
    protected: true,
    platformsOnly: true,
    children: [
      {
        path: ROUTES.MUSIC_ASSET_SCRAPER,
        element: MusicAssetScraper,
        protected: true,
        platformsOnly: true,
      },
      {
        path: ROUTES.MUSIC_ASSET_SCRAPER_EXPLORER,
        element: MusicAssetScraperExplorer,
        protected: true,
        platformsOnly: true,
      },
    ],
  },

  {
    path: ROUTES.BANNER_SWAP,
    element: LegacyBannerSwapLayout,
    protected: true,
    children: [
      {
        index: true,
        element: RedirectLegacyBannerToDefaultBanners,
      },
      {
        path: "channel_banners",
        element: RedirectLegacyBannerToDefaultBanners,
      },
      {
        path: "swap_queue",
        element: RedirectLegacyBannerToSwapQueue,
      },
      {
        path: "*",
        element: RedirectLegacyBannerToDashboard,
      },
    ],
  },
  {
    path: ROUTES.BANNER_DASHBOARD,
    element: BannerDashboard,
    protected: true,
    children: [
      {
        index: true,
        element: BannerDashboardIndexRedirect,
      },
      {
        path: "default_banners",
        element: DefaultBannersTab,
      },
      {
        path: "swap_queue",
        element: SwapQueueTab,
      },
      {
        path: "calendar",
        element: CalendarTab,
      },
    ],
  },
  {
    path: ROUTES.CLASSIFIERS,
    element: Classifiers,
    protected: true,
    children: [
      {
        index: true,
        element: MusicClassifierTab,
      },
      {
        path: "music_classifier",
        element: MusicClassifierTab,
      },
      {
        path: "music_classifier_history",
        element: MusicClassifierHistoryTab,
      },
      {
        path: "video_auditor",
        element: VideoAuditorTab,
      },
      {
        path: "bulk",
        element: BulkAnalysisTab,
      },
    ],
  },
  {
    path: ROUTES.METADATA_AUDIT,
    element: MetadataAudit,
    protected: true,
    children: [
      {
        index: true,
        element: MetadataAuditIndexRedirect,
      },
      {
        path: "title_qc",
        element: TitleSpellingTab,
      },
      {
        path: "title_spelling",
        element: MetadataAuditTitleSpellingRedirect,
      },
      {
        path: "dictionary",
        element: DictionarySettingsTab,
      },
      {
        path: "admin",
        element: SpellcheckAdminTab,
      },
    ],
  },
  {
    path: ROUTES.OAC_AUDIT,
    element: OACAudit,
    protected: true,
    children: [
      {
        index: true,
        element: OACAuditTab,
      },
      {
        path: "channel_settings",
        element: ChannelSettingsTab,
      },
      {
        path: "playlists",
        element: PlaylistCreationTab,
      },
    ],
  },
  {
    path: ROUTES.YOUTUBE_SCORECARD,
    element: YouTubeScorecard,
    protected: true,
    children: [
      {
        path: "metadata",
        element: MetadataTab,
        // Don't add protected here - parent is already protected
        // Adding protected wraps in ProtectedRoute which breaks Outlet context
      },
      {
        path: "claims_ads",
        element: ClaimsAdsTab,
      },
      {
        path: "whitelist",
        element: WhitelistTab,
      },
      {
        path: "custom_id_lookup",
        element: CustomIdLookupTab,
      },
      {
        path: "reference_status",
        element: ReferenceStatusTab,
      },
    ],
  },
  {
    path: ROUTES.YOUTUBE_SCORECARD_MOONBUG_MUSIC_LABEL,
    element: MoonbugMusicLabelScorecard,
    protected: true,
  },
  {
    path: ROUTES.CHANNEL_CHECKUP,
    element: ChannelCheckup,
    protected: true,
    platformsOnly: true,
  },
  {
    path: ROUTES.LABELS_DASHBOARD,
    element: LabelsDashboard,
    protected: true,
  },
  {
    path: ROUTES.SPOTIFY_PODCAST_UPLOADER,
    element: SpotifyPodcastUploader,
    protected: true,
  },
  {
    path: ROUTES.ADMIN_TOOLS,
    element: AppManagment,
    protected: true,
    adminOnly: true,
  },
  {
    path: ROUTES.BANNER_SWAP_ADMIN_OPS,
    element: BannerSwapAdminOps,
    protected: true,
    adminOnly: true,
  },
  {
    path: ROUTES.NOT_FOUND,
    element: NotFound,
    protected: false,
  },
];

// Generate route elements for React Router
export const generateRouteElements = () => {
  return ROUTE_CONFIG_WITH_COMPONENTS.map((route) => {
    const routeElement = {
      path: route.path,
      element: route.element,
      children: route.children?.map((child) => ({
        path: child.path,
        element: child.element,
      })),
    };

    return routeElement;
  });
};
